import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Submission } from '../submission.js';
import { persistGrammarFiOutcomes } from './persist-grammar-fi-outcomes.js';

export const addGrammarErrorMutation = registerMutation({
  operationName: 'addGrammarErrorMutation',
  type: () => Boolean,
  params: [
    { name: 'submissionId', type: () => String },
    { name: 'mainTaskId', type: () => String },
    { name: 'errorId', type: () => String },
    { name: 'category', type: () => String },
    { name: 'ruleId', type: () => String },
    { name: 'message', type: () => String },
    { name: 'boxX', type: () => Float },
    { name: 'boxY', type: () => Float },
    { name: 'boxWidth', type: () => Float },
    { name: 'boxHeight', type: () => Float },
    { name: 'boxPage', type: () => Float },
  ],
  resolve: (
    submissionId: string,
    mainTaskId: string,
    errorId: string,
    category: string,
    ruleId: string,
    message: string,
    boxX: number,
    boxY: number,
    boxWidth: number,
    boxHeight: number,
    boxPage: number,
  ) =>
    async () => {
      try {
        const col = await getRawCollection(Submission._collectionName);
        const submission = await col.findOne({ _id: submissionId } as any);
        if (!submission) return false;

        const mainTaskIndex = (submission as any).taskSubmissions?.findIndex(
          (t: any) => String(t._id) === String(mainTaskId),
        );
        if (mainTaskIndex === undefined || mainTaskIndex === -1) return false;

        const path = `taskSubmissions.${mainTaskIndex}.grammarEvaluation`;

        // Coordinates come in as 0-100 percentages; DB stores ×10 (0-1000).
        // pageNumber in DB is 1-based; boxPage from the view is 0-based.
        const newError = {
          _class: 'GrammarError',
          _id: errorId,
          offset: 0,
          length: 0,
          message,
          ruleId,
          category,
          boundingBox: {
            _class: 'BoundingBox',
            _id: `${errorId}-box`,
            pageNumber: boxPage + 1,
            contentType: 'text',
            topLeft: { _class: 'Point', x: boxX * 10, y: boxY * 10 },
            bottomRight: {
              _class: 'Point',
              x: (boxX + boxWidth) * 10,
              y: (boxY + boxHeight) * 10,
            },
          },
        };

        await col.updateOne(
          { _id: submissionId } as any,
          { $push: { [`${path}.errors`]: newError } } as any,
        );

        // Recalculate errorCount and fehlerindex after addition.
        const updated = await col.findOne({ _id: submissionId } as any);
        const evaluation = (updated as any)?.taskSubmissions?.[mainTaskIndex]?.grammarEvaluation;
        const errors: any[] = evaluation?.errors ?? [];
        const wordCount: number = evaluation?.wordCount ?? 0;
        const errorCount = errors.length;
        const fehlerindex = wordCount > 0 ? (errorCount / wordCount) * 100 : 0;

        await col.updateOne(
          { _id: submissionId } as any,
          {
            $set: { [`${path}.errorCount`]: errorCount, [`${path}.fehlerindex`]: fehlerindex },
          } as any,
        );

        // The error count feeds the per-student Fehlerindex deduction — recompute it (fire-and-forget).
        const examId = String((submission as any).examId ?? '');
        if (examId) void persistGrammarFiOutcomes(examId);

        return true;
      } catch (err) {
        logger.error(
          { err, submissionId, mainTaskId, errorId, context: 'ADD_GRAMMAR_ERROR' },
          'Failed to add grammar error',
        );
        return false;
      }
    },
});
